import trayStore from "data/trayStore.json";
import { auth } from "services/firebase";

const trayAdminHeaders = async () => {
  if (!auth?.currentUser) throw new Error("Entre com sua conta administrativa do PSO.");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${await auth.currentUser.getIdToken()}`,
  };
};

const traySessionStorageKey = `tray_auth_session_${trayStore.storeId}`;
const trayStoreHostStorageKey = `tray_store_host_${trayStore.storeId}`;

export const clearLegacyTrayCredentials = () => {
  if (typeof window === "undefined") return;
  try {
    for (const key of Object.keys(window.localStorage)) {
      if (key === "tray_auth_session" || key.startsWith("tray_auth_session_"))
        window.localStorage.removeItem(key);
    }
  } catch (_) {
    // Storage may be unavailable; legacy cleanup must never prevent PSO login.
  }
};

const normalizeUrl = (url) =>
  String(url || "")
    .trim()
    .replace(/\/+$/, "");

const getStoredJson = (key) => {
  if (typeof window === "undefined") return null;

  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return null;
  }
};

const setStoredJson = (key, value) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, JSON.stringify(value));
};

export const storeTrayStoreHost = (storeHost) => {
  if (typeof window === "undefined") return;

  const normalizedStoreHost = normalizeUrl(storeHost);

  if (normalizedStoreHost) {
    window.sessionStorage.setItem(trayStoreHostStorageKey, normalizedStoreHost);
  }
};

// Compatibility helpers retain only connection metadata, never Tray credentials.
export const saveTraySession = (session) => {
  const safeSession = {
    store_id: String(session?.storeId || session?.store_id || trayStore.storeId),
    isAuthenticated: session?.isAuthenticated === true,
    saved_at: new Date().toISOString(),
  };
  setStoredJson(traySessionStorageKey, safeSession);
  return safeSession;
};
export const getTraySession = () => {
  const session = getStoredJson(traySessionStorageKey);
  return String(session?.store_id) === trayStore.storeId ? saveTraySession(session) : null;
};
export const hasTraySession = () => getTraySession()?.isAuthenticated === true;

export const getTrayConfig = async () => {
  const response = await fetch("/api/tray/config", {
    headers: await trayAdminHeaders(),
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error("Nao foi possivel carregar a configuracao da Tray.");
  }

  const contentType = response.headers.get("content-type") || "";

  if (!contentType.includes("application/json")) {
    throw new Error("A API da Tray local nao respondeu. Rode o app pelo server.js.");
  }

  const config = await response.json();
  let host;
  try {
    host = new URL(config.storeHost).host;
  } catch (error) {
    host = "";
  }
  if (!trayStore.allowedApiHosts.includes(host) || String(config.storeId) !== trayStore.storeId) {
    throw new Error(
      `O servidor ainda nao foi configurado para a nova loja Tray ${trayStore.storeId}. Atualize a configuracao e o backend na AWS.`
    );
  }
  return config;
};

export const checkTrayAuthentication = async () => {
  const response = await fetch("/api/tray/auth-check", {
    headers: await trayAdminHeaders(),
    cache: "no-store",
  });
  const body = await response.json().catch(() => ({}));

  if (!response.ok || !body?.isAuthenticated) {
    const details = body?.details ? ` Detalhes: ${JSON.stringify(body.details)}` : "";
    throw new Error(`${body?.message || "Nao foi possivel autenticar com a Tray."}${details}`);
  }

  return body;
};

export const buildTrayAuthorizationUrl = ({ consumerKey, callbackUrl, storeHost }) => {
  const normalizedStoreHost = normalizeUrl(storeHost);

  if (!normalizedStoreHost || !consumerKey || !callbackUrl) {
    return "";
  }

  const params = new URLSearchParams({
    response_type: "code",
    consumer_key: consumerKey,
    callback: callbackUrl,
  });

  return `${normalizedStoreHost}/auth.php?${params.toString()}`;
};

export const redirectToTrayAuthorization = async () => {
  const config = await getTrayConfig();

  if (!config.isConfigured) {
    throw new Error("As credenciais da Tray nao estao configuradas no servidor.");
  }

  // The backend owns the active store. Cached hosts must not override a store migration.
  const storeHost = normalizeUrl(config.storeHost);
  storeTrayStoreHost(storeHost);
  const authorizationUrl = buildTrayAuthorizationUrl({
    consumerKey: config.consumerKey,
    callbackUrl: config.callbackUrl,
    storeHost,
  });

  if (!authorizationUrl) {
    return false;
  }

  window.location.assign(authorizationUrl);
  return true;
};

export const exchangeTrayAuthorizationCode = async ({ apiAddress, code, store }) => {
  const response = await fetch("/api/tray/tokens", {
    method: "POST",
    headers: await trayAdminHeaders(),
    body: JSON.stringify({ apiAddress, code }),
  });
  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    const details = body?.details ? ` Detalhes: ${JSON.stringify(body.details)}` : "";
    throw new Error(`${body?.message || "Nao foi possivel autenticar com a Tray."}${details}`);
  }

  return saveTraySession(body);
};
